import React, { useState, useEffect, useMemo, useRef } from "react";
import clsx from "clsx";
import Topbar from "../Topbar/Topbar";
import SideBar, { normalizeSidebarData } from "../SideBar/SideBar";
import { useNavigate, useParams, useLocation } from "react-router-dom";

// Define a compatible SideBarItem type with index signature
type SideBarItem = {
  to?: string;
  label: string;
  icon?: React.ReactNode;
  subItems?: SideBarItem[];
  [key: string]: unknown; // <-- Adicione esta linha
};
import DynamicRendererLoader from "../DynamicRenderer/DynamicRendererLoader";
import type { DynamicComponentConfig } from "../DynamicRenderer/DynamicRendererLoader"; // <-- Import type
import { AnimatePresence, motion } from "framer-motion";
import type { NotificationType, NotificationProps } from "../Notification/Notification";
import Calendar from "../Calendar/Calendar"; // ajuste o caminho se necessário
import Loader from "../Loader/Loader"; // ajuste o caminho se necessário

// (Removed duplicate SideBarItem type definition)

// Ensure OriginalSideBarItem has an index signature for compatibility
// (Removed invalid module augmentation block)

import Breadcrumbs from "../Breadcrumbs/Breadcrumbs";
import { ToastProvider } from "../Toast/ToastContext";
import { useThemeContext } from "../../../context/ThemeContext";
import { useAccentColorStore } from "../../../store/useAccentColorStore"; // importe o store
import Menu from "../Menu/Menu"; // Adicione este import
import Button from "../Button/Button"; // adicione este import
import { useGlobalStore } from "../../../store/useGlobalStore";

type MenuItem = SideBarItem;

interface AppLayoutProps {
  menu: MenuItem[];
  selectedMenu?: string;
  onMenuSelect?: (key: string) => void;
  renderContent?: (selectedKey: string, menuItem?: MenuItem) => React.ReactNode;
  columns?: number;
  rows?: number;
  theme?: "light" | "dark";
  linkComponent?: React.ComponentType<{ href: string; children: React.ReactNode; [key: string]: unknown }>;
  children?: React.ReactNode;
  dynamicConfigs?: Record<string, unknown>;
  dynamicComponentMap?: Record<string, React.ComponentType<unknown>>;
  dynamicResponsiveClassName?: string;
  dynamicEventPrefix?: string;
  userName?: string; // novo
  userAvatarUrl?: string; // novo
  className?: string;
  mainClassName?: string;
  style?: React.CSSProperties;
  mainStyle?: React.CSSProperties;
  // Notificação
  notifications?: NotificationType[];
  onMarkAsRead?: NotificationProps["onMarkAsRead"];
  onMarkAsUnread?: NotificationProps["onMarkAsUnread"];
  onMarkAsFavorite?: NotificationProps["onMarkAsFavorite"];
  onRemoveFavorite?: NotificationProps["onRemoveFavorite"];
  onOpenNotification?: NotificationProps["onOpen"];
  notificationComponent?: React.ReactNode;
  bellButtonAriaLabel?: string;
  systemName?: string;
  companyName?: string;
  establishmentName?: string;
  clientLogoUrl?: string;
  accentColor?: string; // nova prop opcional (se quiser permitir override via prop)
}

const THEME_CLASSNAMES = {
  light: "bg-neutral-100 text-neutral-900",
  dark: "bg-neutral-900 text-neutral-100",
};

function findMenuPath<
  T extends { to?: string; label: string; subItems?: T[]; [key: string]: unknown }
>(
  items: T[],
  key: string,
  path: Array<{ label: string; key: string }> = []
): Array<{ label: string; key: string }> {
  for (const item of items) {
    if (item.to === key) {
      return [...path, { label: item.label, key: item.to }];
    }
    if (item.subItems) {
      const found = findMenuPath(item.subItems, key, [...path, { label: item.label, key: item.to ?? "" }]);
      if (found.length) return found;
    }
  }
  return [];
}

const AppLayout: React.FC<AppLayoutProps> = ({
  menu,
  selectedMenu: controlledSelectedMenu,
  onMenuSelect,
  renderContent,
  columns = 3,
  rows = 1,
  theme: themeProp,
  linkComponent,
  children,
  dynamicConfigs = {}, // <-- valor padrão adicionado aqui
  dynamicComponentMap = {}, // <-- add default value here
  dynamicResponsiveClassName,
  dynamicEventPrefix,
  userName = "Usuário",
  userAvatarUrl,
  className,
  mainClassName,
  style,
  mainStyle,
  notifications,
  onMarkAsRead,
  onMarkAsUnread,
  onMarkAsFavorite,
  onRemoveFavorite,
  onOpenNotification,
  notificationComponent,
  bellButtonAriaLabel,
  systemName,
  companyName,
  establishmentName,
  clientLogoUrl,
  accentColor, // nova prop opcional (se quiser permitir override via prop)
}) => {
  const { theme: contextTheme, setTheme } = useThemeContext?.() ?? {};
  const theme = themeProp || contextTheme || "light";
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [uncontrolledSelectedMenu, setUncontrolledSelectedMenu] = useState(menu[0]?.to ?? "");
  const [showCalendar, setShowCalendar] = useState(false);
  const [userMenuOpen, setUserMenuOpen] = useState(false); // Estado para abrir/fechar o menu de usuário
  const [isLoading, setIsLoading] = useState(false); // Estado de loading global (você pode controlar isso conforme sua lógica)
  const selectedMenu = controlledSelectedMenu ?? uncontrolledSelectedMenu;
  const storeAccentColor = useAccentColorStore((s) => s.accentColor);
  const accent = accentColor || storeAccentColor;

  const userMenuButtonRef = useRef<HTMLButtonElement>(null);

  // Pegue o usuário do store global
  const user = useGlobalStore((s) => s.user);

  // Exemplo de uso: se userName não for passado via prop, use do store
  const effectiveUserName = userName ?? user?.name ?? "Usuário";
  const effectiveUserAvatarUrl = userAvatarUrl ?? user?.avatarUrl;

  // Exemplo de ações do usuário
  const userMenuItems = [
    {
      label: "Meu Perfil",
      onClick: () => {
        setUserMenuOpen(false);
        // navegue para perfil
      },
    },
    {
      label: "Trocar Empresa",
      onClick: () => {
        setUserMenuOpen(false);
        // lógica de troca de empresa
      },
    },
    {
      type: "separator" as const, // <-- CORREÇÃO AQUI
    },
    {
      label: "Sair",
      danger: true,
      onClick: () => {
        setUserMenuOpen(false);
        // lógica de logout
      },
    },
  ];

  const navigate = useNavigate();
  const params = useParams();
  const location = useLocation();

  const query = useMemo(() => {
    const searchParams = new URLSearchParams(location.search);
    const obj: Record<string, string> = {};
    for (const [key, value] of searchParams.entries()) {
      obj[key] = value;
    }
    return obj;
  }, [location.search]);

  useEffect(() => {
    if (controlledSelectedMenu) {
      setUncontrolledSelectedMenu(controlledSelectedMenu);
    }
  }, [controlledSelectedMenu]);

  useEffect(() => {
    if (!controlledSelectedMenu && menu.length && !menu.find(m => m.to === uncontrolledSelectedMenu)) {
      setUncontrolledSelectedMenu(menu[0]?.to ?? "");
    }
  }, [menu, controlledSelectedMenu, uncontrolledSelectedMenu]);

  const menuItems = normalizeSidebarData(menu, {
    labelKey: "label",
    iconKey: "icon",
    toKey: "to",
    subItemsKey: "subItems",
  });

  const breadcrumbItems = useMemo(() => {
    const path = findMenuPath(menuItems, selectedMenu);
    return path.length
      ? path
      : [
          {
            label: menuItems.find(i => i.to === selectedMenu)?.label || selectedMenu,
            key: selectedMenu,
          },
        ];
  }, [menuItems, selectedMenu]);

  const handleSidebarItemClick = (to: string) => {
    if (onMenuSelect) onMenuSelect(to);
    else setUncontrolledSelectedMenu(to);
    setSidebarOpen(false);
    navigate(`/painel/${to}`); // Atualiza a URL
  };

  const handleBreadcrumbClick = (key: string) => {
    if (onMenuSelect) onMenuSelect(key);
    else setUncontrolledSelectedMenu(key);
  };

  const mainRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    mainRef.current?.focus();
  }, [selectedMenu]);

  useEffect(() => {
    if (mainRef.current) {
      mainRef.current.scrollTo?.(0, 0);
      // fallback para navegadores antigos
      mainRef.current.scrollTop = 0;
    }
  }, [selectedMenu]);

  // Exemplo: ative o loader durante navegação ou carregamento dinâmico
  // Você pode adaptar para usar um store global ou lógica de fetch
  useEffect(() => {
    // Exemplo fictício: simula carregamento ao trocar de menu
    setIsLoading(true);
    const timeout = setTimeout(() => setIsLoading(false), 400); // ajuste conforme necessário
    return () => clearTimeout(timeout);
  }, [selectedMenu]);

  return (
    <ToastProvider>
      <div
        className={clsx(
          "min-h-screen flex flex-col",
          THEME_CLASSNAMES[theme],
          "pt-[56px]",
          className
        )}
        style={{
          ...style,
          "--accent-color": accent, // define a CSS variable global
        } as React.CSSProperties}
      >
        {/* Loader global */}
        {isLoading && (
          <Loader
            overlay
            message="Carregando..."
            theme={theme}
            accentColor={accent}
            fallbackType="dots" // Use "spinner" ou "dots" conforme UX desejada
            responsive
            delayMs={200} // Evita flicker em navegação rápida
            ariaLabel="Carregando painel"
          >
            {/* Exemplo de children customizado (opcional) */}
            <span className={clsx("mt-2", theme === "dark" ? "text-cyan-200" : "text-blue-700")}>
              Aguarde, estamos preparando tudo para você!
            </span>
          </Loader>
        )}

        <Topbar
          title={menuItems.find(i => i.to === selectedMenu)?.label || ""}
          userName={effectiveUserName}
          avatarUrl={effectiveUserAvatarUrl}
          onMenuClick={() => setSidebarOpen(true)}
          theme={theme}
          onThemeToggle={setTheme ? () => setTheme(theme === "dark" ? "light" : "dark") : undefined}
          notifications={notifications ?? []}
          onMarkAsRead={onMarkAsRead}
          onMarkAsUnread={onMarkAsUnread}
          onMarkAsFavorite={onMarkAsFavorite}
          onRemoveFavorite={onRemoveFavorite}
          onOpen={onOpenNotification}
          notificationComponent={notificationComponent}
          bellButtonAriaLabel={bellButtonAriaLabel}
          calendarButton={
            <Button
              onClick={() => setShowCalendar((v) => !v)}
              className={clsx(
                "ml-2 rounded-full p-2 flex items-center justify-center border-0 transition-all duration-200 shadow",
                theme === "light"
                  ? "bg-white hover:bg-blue-50 active:bg-blue-100 text-blue-600"
                  : "bg-slate-800 hover:bg-slate-700 active:bg-slate-600 text-cyan-200"
              )}
              title={showCalendar ? "Fechar calendário" : "Abrir calendário"}
              type="button"
              ariaLabel={showCalendar ? "Fechar calendário" : "Abrir calendário"}
              variant="icon"
              size="tiny"
            >
              {/* Ícone de calendário */}
              <svg width="20" height="20" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24" aria-hidden="true">
                <rect x="3" y="4" width="18" height="18" rx="4" />
                <path d="M16 2v4M8 2v4M3 10h18" />
              </svg>
            </Button>
          }
          userActions={
            <>
              <Button
                ref={userMenuButtonRef}
                onClick={() => setUserMenuOpen((v) => !v)}
                className={clsx(
                  "ml-2 rounded-full p-2 flex items-center justify-center border-0 transition-all duration-200",
                  theme === "light"
                    ? "bg-white hover:bg-blue-50 active:bg-blue-100 text-blue-600"
                    : "bg-slate-800 hover:bg-slate-700 active:bg-slate-600 text-cyan-200"
                )}
                ariaLabel="Abrir menu do usuário"
                type="button"
                variant="icon"
                size="tiny"
              >
                <svg width="20" height="20" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                  <circle cx="12" cy="7" r="4" />
                  <path d="M5.5 21a7.5 7.5 0 0 1 13 0" />
                </svg>
              </Button>
              <Menu
                open={userMenuOpen}
                anchorEl={userMenuButtonRef as React.RefObject<HTMLElement>}
                items={userMenuItems}
                onClose={() => setUserMenuOpen(false)}
                theme={theme}
              />
            </>
          }
          systemName={systemName}
          companyName={companyName}
          establishmentName={establishmentName}
          clientLogoUrl={clientLogoUrl}
        />
        <Breadcrumbs
          items={breadcrumbItems}
          onClick={handleBreadcrumbClick}
          theme={theme}
        />
        <SideBar
          open={sidebarOpen}
          onClose={() => setSidebarOpen(false)}
          items={menuItems.map(item => ({
            ...item,
            onClick: () => handleSidebarItemClick(item.to!),
            "aria-current": item.to === selectedMenu ? "page" : undefined,
          }))}
          theme={theme}
          linkComponent={linkComponent}
          selectedMenu={selectedMenu}
          logoUrl={clientLogoUrl} // <-- adicione esta linha
          logoAlt={companyName || systemName || "Logo"} // <-- adicione esta linha
        />
        <main
          ref={mainRef}
          role="main"
          tabIndex={-1}
          className={clsx(
            "flex-1 p-4 w-full max-w-full overflow-x-auto",
            columns > 1 && "grid gap-4",
            mainClassName,
            "fluent-background"
          )}
          style={{
            ...(columns > 1 || rows > 1
              ? {
                  display: "grid",
                  gridTemplateColumns: columns > 1 ? `repeat(${columns}, minmax(0, 1fr))` : undefined,
                  gridTemplateRows: rows > 1 ? `repeat(${rows}, minmax(0, 1fr))` : undefined,
                }
              : {}),
            ...mainStyle,
          }}
        >
          <AnimatePresence mode="wait">
            <motion.div
              key={selectedMenu + (showCalendar ? "-calendar" : "")}
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -16 }}
              transition={{ duration: 0.25 }}
              className="h-full w-full"
            >
              {showCalendar ? (
                <Calendar theme={theme} />
              ) : renderContent
                ? renderContent(
                    selectedMenu,
                    {
                      ...(menuItems.find(i => i.to === selectedMenu) as MenuItem | undefined),
                      label: menuItems.find(i => i.to === selectedMenu)?.label ?? "",
                      params,
                      query,
                      location
                    }
                  )
                : dynamicConfigs && dynamicConfigs[selectedMenu]
                  ? (
                      <DynamicRendererLoader
                        config={dynamicConfigs[selectedMenu] as DynamicComponentConfig[] | undefined}
                        columns={columns}
                        rows={rows}
                        theme={theme}
                        params={params}
                        query={query}
                        location={location}
                        // Passe todas as props extras para permitir customização total
                        {...(typeof dynamicConfigs[selectedMenu] === "object" ? dynamicConfigs[selectedMenu] : {})}
                        {...(typeof dynamicComponentMap === "object" ? { componentMap: dynamicComponentMap } : {})}
                        {...(typeof dynamicResponsiveClassName === "string" ? { responsiveClassName: dynamicResponsiveClassName } : {})}
                        {...(typeof dynamicEventPrefix === "string" ? { eventPrefix: dynamicEventPrefix } : {})}
                        {...(typeof userName === "string" ? { userName } : {})}
                        {...(typeof userAvatarUrl === "string" ? { userAvatarUrl } : {})}
                        {...(typeof mainClassName === "string" ? { mainClassName } : {})}
                        {...(typeof mainStyle === "object" ? { mainStyle } : {})}
                        {...(typeof style === "object" ? { style } : {})}
                        {...(typeof className === "string" ? { className } : {})}
                        // Passe todas as outras props do AppLayout
                        {...(typeof renderContent === "function" ? { renderContent } : {})}
                        {...(linkComponent ? { linkComponent } : {})}
                        {...(children ? { children } : {})}
                      />
                    )
                  : children
                    ? children
                    : (
                      <div className="text-center text-gray-400 py-12">
                        Nenhum conteúdo disponível.
                      </div>
                    )
              }
            </motion.div>
          </AnimatePresence>
        </main>
      </div>
    </ToastProvider>
  );
};

export default AppLayout;

